import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Plus, Copy, Check, Ban, Users, UserPlus, Calendar, Mail, Phone } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAuth as useAuthHook } from '../hooks/useAuth';
import { invitesApi, type Invite, type WaitlistEntry } from '../lib/api/invites';
import TopBar from '../components/TopBar';
import { createAdminInvitesMobileMenu } from '../components/MobileContextMenu';
import Button from '../components/Button';
import EmptyState from '../components/EmptyState';
import AdminInviteModal from '../components/AdminInviteModal';

type TabType = 'invites' | 'waitlist';

const AdminInvitesPage = () => {
  const navigate = useNavigate();
  const { me } = useAuth();
  const { logout } = useAuthHook();
  const [activeTab, setActiveTab] = useState<TabType>('invites');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Redirect non-admins
  useEffect(() => {
    if (me && !me.is_platform_admin) {
      window.location.href = '/';
    }
  }, [me]);
  
  // Create mobile menu configuration
  const adminInvitesMenu = createAdminInvitesMobileMenu(navigate, logout, setShowCreateModal);

  // Fetch invites
  const { data: invitesData, isLoading: invitesLoading, refetch: refetchInvites } = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => invitesApi.listInvites({}),
    enabled: activeTab === 'invites'
  });

  // Fetch waitlist
  const { data: waitlistData, isLoading: waitlistLoading } = useQuery({
    queryKey: ['admin', 'waitlist'],
    queryFn: () => invitesApi.viewWaitlist({}),
    enabled: activeTab === 'waitlist'
  });

  const handleCopyLink = async (inviteId: string, inviteCode: string) => {
    try {
      const inviteUrl = `${window.location.origin}/invite/${inviteCode}`;
      await navigator.clipboard.writeText(inviteUrl);
      setCopiedId(inviteId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Show user-friendly error message
      alert('Failed to copy invite link');
    }
  };

  const handleRevoke = async (inviteId: string) => {
    if (!confirm('Are you sure you want to revoke this invite?')) return;
    
    try {
      await invitesApi.revokeInvite(inviteId);
      refetchInvites();
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to revoke invite:', err);
      }
      alert('Unable to revoke invite');
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-400 bg-yellow-500/10';
      case 'accepted':
        return 'text-green-400 bg-green-500/10';
      case 'expired':
        return 'text-text-muted bg-oro-surface-alt';
      case 'revoked':
        return 'text-red-400 bg-red-500/10';
      default:
        return 'text-text-secondary bg-oro-surface-alt';
    }
  };

  if (!me?.is_platform_admin) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center p-4">
        <div className="bg-oro-surface border border-oro-border rounded-xl p-6 text-center">
          <p className="text-text-secondary">Access denied. Platform admin required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-oro-bg">
      <TopBar 
        title="Admin Dashboard" 
        onBack={() => window.history.back()}
        mobileMenu={adminInvitesMenu}
      />

        <div className="safe-area-top pt-4 px-4 pb-20">
          {/* Header with Create Button */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-sm text-text-muted">Manage invites and waitlist</p>
            </div>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowCreateModal(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create
            </Button>
          </div>
        {/* Tab Navigation */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-1 mb-6">
          <div className="grid grid-cols-2 gap-1">
            <button
              onClick={() => setActiveTab('invites')}
              className={`px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'invites'
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-text-muted hover:text-text-secondary hover:bg-oro-surface-alt'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">Invites</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('waitlist')}
              className={`px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'waitlist'
                  ? 'bg-accent text-white shadow-sm'
                  : 'text-text-muted hover:text-text-secondary hover:bg-oro-surface-alt'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Waitlist</span>
              </div>
            </button>
          </div>
        </div>

        {/* Invites Tab */}
        {activeTab === 'invites' && (
          <div>
            {invitesLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-oro-surface border border-oro-border rounded-xl p-4 animate-pulse">
                    <div className="h-4 bg-oro-surface-alt rounded w-1/3 mb-3"></div>
                    <div className="h-3 bg-oro-surface-alt rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-oro-surface-alt rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : invitesData?.invites?.length === 0 ? (
              <EmptyState
                icon={<UserPlus className="w-6 h-6 text-text-muted" />}
                title="No Invites Yet"
                message="Create your first invite to get started"
                actionLabel="Create First Invite"
                onAction={() => setShowCreateModal(true)}
              />
            ) : (
              <div className="space-y-4">
                {invitesData?.invites?.map((invite: Invite) => (
                  <div key={invite.id} className="bg-oro-surface border border-oro-border rounded-xl p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-text-primary capitalize">
                            {invite.type.replace('_', ' ')}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${getStatusColor(invite.status)}`}>
                            {invite.status}
                          </span>
                        </div>
                        
                        {/* Contact Info */}
                        {(invite.invitee_email || invite.invitee_phone) && (
                          <div className="flex items-center gap-1.5 mb-2">
                            {invite.invitee_email ? (
                              <>
                                <Mail className="w-3.5 h-3.5 text-text-muted" />
                                <span className="text-sm text-text-secondary">{invite.invitee_email}</span>
                              </>
                            ) : (
                              <>
                                <Phone className="w-3.5 h-3.5 text-text-muted" />
                                <span className="text-sm text-text-secondary">{invite.invitee_phone}</span>
                              </>
                            )}
                          </div>
                        )}
                        
                        {/* Club */}
                        {invite.club_name && (
                          <div className="text-sm text-text-muted">
                            Club: {invite.club_name}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCopyLink(invite.id, invite.code)}
                          className="p-2 hover:bg-oro-surface-alt rounded-lg transition-colors"
                          title="Copy invite link"
                        >
                          {copiedId === invite.id ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4 text-text-muted" />
                          )}
                        </button>
                        {invite.status === 'pending' && (
                          <button
                            onClick={() => handleRevoke(invite.id)}
                            className="p-2 hover:bg-oro-surface-alt rounded-lg transition-colors"
                            title="Revoke invite"
                          >
                            <Ban className="w-4 h-4 text-text-muted hover:text-red-400" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-4 text-xs text-text-muted">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        <span>Created {formatDate(invite.created_at)}</span>
                      </div>
                      {invite.expires_at && (
                        <div className="flex items-center gap-1">
                          <span>Expires {formatDate(invite.expires_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Waitlist Tab */}
        {activeTab === 'waitlist' && (
          <div>
            {waitlistLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-oro-surface border border-oro-border rounded-xl p-4 animate-pulse">
                    <div className="h-4 bg-oro-surface-alt rounded w-1/3 mb-3"></div>
                    <div className="h-3 bg-oro-surface-alt rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-oro-surface-alt rounded w-2/3"></div>
                  </div>
                ))}
              </div>
            ) : waitlistData?.waitlist?.length === 0 ? (
              <EmptyState
                icon={<Users className="w-6 h-6 text-text-muted" />}
                title="No Waitlist Entries"
                message="Waitlist entries will appear here when people join"
              />
            ) : (
              <div className="space-y-4">
                {waitlistData?.waitlist?.map((entry: WaitlistEntry) => (
                  <div key={entry.id} className="bg-oro-surface border border-oro-border rounded-xl p-4">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        {/* Contact Info */}
                        <div className="flex items-center gap-1.5 mb-2">
                          {entry.email ? (
                            <>
                              <Mail className="w-3.5 h-3.5 text-text-muted" />
                              <span className="text-sm font-medium text-text-primary">{entry.email}</span>
                            </>
                          ) : (
                            <>
                              <Phone className="w-3.5 h-3.5 text-text-muted" />
                              <span className="text-sm font-medium text-text-primary">{entry.phone}</span>
                            </>
                          )}
                        </div>
                        
                        {/* Source */}
                        {entry.source && (
                          <div className="text-sm text-text-muted capitalize mb-2">
                            Source: {entry.source.replace('_', ' ')}
                          </div>
                        )}
                      </div>

                      {/* Status */}
                      <div>
                        {entry.invited === 1 ? (
                          <span className="text-xs px-2 py-1 bg-green-500/10 text-green-400 rounded-full font-medium">
                            Invited
                          </span>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-yellow-500/10 text-yellow-400 rounded-full font-medium">
                            Pending
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Metadata */}
                    <div className="flex items-center gap-1 text-xs text-text-muted">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>Joined {formatDate(entry.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create Invite Modal */}
      <AdminInviteModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          refetchInvites();
        }}
      />
    </div>
  );
};

export default AdminInvitesPage;
